import { useAccommodationDetailSuspenseQuery, useUnitDiscountQuery } from '@/api/accommodation/hooks'
import { useCallback } from 'react'
import { trackAccommodationTimeSpent, trackAccommodationViewed } from '@/lib/track'
import { useTrackOnce } from '@/hooks/useTrackOnce'
import { useVisibleTimeTracking } from '@/hooks/useVisibleTimeTracking'
import { useIsMobile } from '@/hooks/useIsMobile'
import { unitFullName } from '@/lib/unit'
import Divider from '@/components/divider'
import { AccommodationDetailFixedTopBar } from './AccommodationDetailFixedTopBar'
import { DetailHeroSection } from './hero/DetailHeroSection'
import { DetailTitleSection } from './hero/DetailTitleSection'
import { DetailUnitSwitcher } from './DetailUnitSwitcher'
import { DetailSectionNav } from './DetailSectionNav'
import { DetailHighlightsSection } from './DetailHighlightsSection'
import { DetailAboutSection } from './DetailAboutSection'
import { DetailAmenitiesSection } from './amenities/DetailAmenitiesSection'
import { DetailConditionsSection } from './conditions/DetailConditionsSection'
import { DetailLocationSection } from './location/DetailLocationSection'
import { DetailTripsSection } from './trips/DetailTripsSection'
import { DetailBreadcrumb, DetailLandingLinks } from './DetailLandingLinks'
import { DateSelectionProvider } from './date-selection/DateSelectionProvider'
import { DateSelectionDesktop } from './date-selection/DateSelectionDesktop'
import { DateSelectionMobile } from './date-selection/DateSelectionMobile'

interface AccommodationDetailContentProps {
  slug: string
  unitSlug: string
}

export function AccommodationDetailContent({ slug, unitSlug }: AccommodationDetailContentProps) {
  const { data } = useAccommodationDetailSuspenseQuery(slug, unitSlug)
  const { discount, isFetched: isAvailabilityFetched } = useUnitDiscountQuery(data.unit.id)
  const isMobile = useIsMobile()

  // Fire once availability has settled so the event carries the daily "from"
  // price (value + currency for Meta). Gated on isFetched — not on discount —
  // so the event still fires for accommodations with no current availability.
  useVisibleTimeTracking(useCallback((seconds: number) => trackAccommodationTimeSpent(data, seconds), [data]))

  useTrackOnce(
    () => {
      trackAccommodationViewed(
        data,
        discount
          ? {
              dailyDiscountPriceFrom: discount.minPrice,
              dailyStandardPriceFrom: discount.maxPrice,
            }
          : undefined,
      )
    },
    [data, discount, isAvailabilityFetched],
    isAvailabilityFetched,
  )

  return (
    <DateSelectionProvider>
      <div>
        {/* Mobile: fixed top bar with back arrow and share button */}
        {isMobile && (
          <AccommodationDetailFixedTopBar name={unitFullName(data, data.unit)} slug={data.slug} unitId={data.unit.id} />
        )}

        {/* pb-24 on mobile for the fixed bottom bar, lg:pb-12 for desktop */}
        <div className="relative pb-32 lg:pb-12">
          <DetailHeroSection data={data} discount={discount} />

          {/* Two column layout for content + calendar */}
          <div className="relative container grid grid-cols-1 lg:grid-cols-[5fr_4fr] lg:gap-12 lg:pt-8">
            {/* Left column - scrollable content */}
            <div className="order-2 lg:order-1">
              <div className="mt-4 lg:mt-0">
                <DetailBreadcrumb location={data} />
              </div>
              <DetailTitleSection data={data} />
              {!data.unit.sellable && (
                <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-slate-700">
                  Toto ubytování teď nelze rezervovat.
                </p>
              )}
              <DetailUnitSwitcher data={data} />
              {/* Zero-height overlay; appears only once it sticks on scroll */}
              <DetailSectionNav hasTrips={!!data.trips && data.trips.length > 0} />
              <div className="mt-8 space-y-8">
                <DetailHighlightsSection data={data} />
                <Divider size="lg" />
                {/* The unit's own description, often empty, leads the place's. */}
                <DetailAboutSection
                  description={[data.unit.description, data.description.detailed].filter(Boolean).join('\n')}
                />
                <Divider size="lg" />
                <DetailAmenitiesSection unit={data.unit} locationFacility={data.facility} />
                <Divider size="zero" />
                <DetailConditionsSection
                  check={data.check}
                  conditions={data.conditions}
                  surcharges={data.surcharges}
                  business={data.business}
                  surchargePayments={data.surchargePayments}
                />
                <Divider size="lg" />
                <DetailLocationSection location={data} />
                {data.trips && data.trips.length > 0 && (
                  <>
                    <Divider size="lg" />
                    <DetailTripsSection trips={data.trips} />
                  </>
                )}
                <Divider size="lg" />
                <DetailLandingLinks location={data} />
              </div>
            </div>

            {!isMobile && data.unit.sellable && (
              <div className="order-1 lg:sticky lg:top-4 lg:order-2 lg:self-start">
                <div className="mt-8 rounded-xl border border-slate-200 bg-white px-2 py-4 shadow-lg shadow-slate-900/10 md:px-6 lg:mt-0">
                  <DateSelectionDesktop accommodation={data} />
                </div>
              </div>
            )}
          </div>

          {/* Mobile: bottom bar + date selection sheet */}
          {isMobile && data.unit.sellable && <DateSelectionMobile accommodation={data} />}
        </div>
      </div>
    </DateSelectionProvider>
  )
}
